/**
 * Per-position excursion tracking for the Positions columns "Max drawdown" (MAE) and "Max run-up"
 * (MFE). Client-side, from the live mark (SPEC §10.1: open P&L marked at the last trade, Q-U6).
 *
 * Definitions (integer cents, exact):
 * - A position's **lifetime** starts when its account + contract goes from flat to non-zero (or
 *   flips side) and ends when it is flat again. Scaling in or out keeps the same lifetime.
 * - **Max drawdown** = min(0, lowest open P&L observed during the lifetime) — the worst the open
 *   position has been, in $ for the whole position (≤ 0).
 * - **Max run-up** = max(0, highest open P&L observed during the lifetime) (≥ 0).
 * - "Observed" = every open-P&L value this window computed: HELM cannot see prices while it is
 *   closed, so after a reload the tracking restarts from the current value (`sinceMs` says when).
 *   Like NinjaTrader's MAE/MFE, the values are per position, not per contract.
 */
import { SIDE } from "./labels.ts";

export type Excursion = {
  /** +1 long, −1 short (a flip starts a new lifetime). */
  side: 1 | -1;
  /** ≤ 0; null until the first open P&L is known. */
  maeCents: bigint | null;
  /** ≥ 0; null until the first open P&L is known. */
  mfeCents: bigint | null;
  /** When this window started observing the position (epoch ms). */
  sinceMs: number;
};

export type ExcursionObservation = {
  key: string;
  netQty: number;
  /** Current open P&L; null when the mark or tick value is unknown (nothing is recorded). */
  openPnlCents: bigint | null;
};

const min0 = (v: bigint): bigint => (v < 0n ? v : 0n);
const max0 = (v: bigint): bigint => (v > 0n ? v : 0n);

/**
 * Fold one set of observations (every open position right now) into the tracker. Positions that
 * are absent or flat are dropped (their lifetime ended). Returns `prev` itself when nothing
 * changed, so callers can skip work.
 */
export function trackExcursions(
  prev: ReadonlyMap<string, Excursion>,
  observations: readonly ExcursionObservation[],
  nowMs: number,
): ReadonlyMap<string, Excursion> {
  const next = new Map<string, Excursion>();
  let changed = false;
  for (const o of observations) {
    if (o.netQty === 0) continue;
    const side: 1 | -1 = o.netQty > 0 ? 1 : -1;
    const p = prev.get(o.key);
    const v = o.openPnlCents;
    if (!p || p.side !== side) {
      next.set(o.key, {
        side,
        maeCents: v === null ? null : min0(v),
        mfeCents: v === null ? null : max0(v),
        sinceMs: nowMs,
      });
      changed = true;
      continue;
    }
    if (v === null) {
      next.set(o.key, p);
      continue;
    }
    const mae = p.maeCents === null ? min0(v) : v < p.maeCents ? v : p.maeCents;
    const mfe = p.mfeCents === null ? max0(v) : v > p.mfeCents ? v : p.mfeCents;
    if (mae !== p.maeCents || mfe !== p.mfeCents) {
      next.set(o.key, { ...p, maeCents: mae, mfeCents: mfe });
      changed = true;
    } else next.set(o.key, p);
  }
  if (!changed && next.size === prev.size) return prev;
  return next;
}

type FillLike = { accountId: string; symbol: string; side: number; qty: number; tsNs: bigint };

/**
 * When the current position in `accountId` + `symbol` was opened: the time of the fill that took
 * it from flat (or the other side) to its current side, replaying `fills` (newest first, as the
 * trading store keeps them) oldest → newest. Null when the replayed quantity does not end at
 * `netQty` — the fills that opened it are older than this gateway session or the 1000-fill window,
 * so the time is unknown rather than guessed.
 */
export function positionOpenedNs(
  fills: readonly FillLike[],
  accountId: string,
  symbol: string,
  netQty: number,
): bigint | null {
  if (netQty === 0) return null;
  let running = 0;
  let opened: bigint | null = null;
  for (let i = fills.length - 1; i >= 0; i--) {
    const f = fills[i];
    if (!f || f.accountId !== accountId || f.symbol !== symbol) continue;
    const signed = f.side === SIDE.BUY ? f.qty : f.side === SIDE.SELL ? -f.qty : 0;
    if (signed === 0) continue;
    const before = running;
    running += signed;
    if (running === 0) opened = null;
    else if (before === 0 || Math.sign(before) !== Math.sign(running)) opened = f.tsNs;
  }
  return running === netQty ? opened : null;
}
